import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { Reveal } from '@/components/motion';
import { SERVICES, IMAGES, TRUST_POINTS } from '@/data/site';
import IconResolver from '@/components/IconResolver';

const ICON_MAP = {
  'ndis-transport': 'Wheelchair',
  'community-transport': 'UsersThree',
  'aged-care-transport': 'HandHeart',
  'hospital-discharge': 'FirstAid',
  'wheelchair-accessible': 'WheelchairMotion',
  'medical-appointment': 'Stethoscope',
  'school-education': 'GraduationCap',
  'group-transport': 'Van',
  'social-community-access': 'Smiley',
  'tac-worksafe': 'ShieldCheck',
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter(s => s.slug !== slug).slice(0, 3);

  return (
    <>
      <section className="pt-16 md:pt-24 pb-16" data-testid={`service-${slug}-hero`}>
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                    <IconResolver name={ICON_MAP[slug]} size={22} className="text-brand" />
                  </div>
                  <div className="overline">Our services</div>
                </div>
                <h1 className="h-display text-balance">{service.name}</h1>
                <p className="body-lg mt-6 max-w-2xl text-pretty">{service.tagline}</p>
                <p className="body-md mt-4 max-w-2xl text-pretty">{service.summary}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link to={`/book?service=${encodeURIComponent(service.name)}`} data-testid={`service-${slug}-book`} className="btn-primary">
                    Book this service <ArrowRight size={16} weight="bold" />
                  </Link>
                  <Link to={`/quote?service=${encodeURIComponent(service.name)}`} data-testid={`service-${slug}-quote`} className="btn-secondary">
                    Get an instant quote
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-neutral-100">
                  <img
                    src={IMAGES[service.image] || IMAGES.hero}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-neutral-50" data-testid={`service-${slug}-details`}>
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="overline mb-5">What's included</div>
              <h2 className="h-section text-balance">Everything you get with {service.name}.</h2>
              <p className="body-md mt-5">Every detail below is built into every trip — no add-ons, no surprises.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {service.bullets.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <div className="p-5 rounded-2xl bg-white border border-neutral-200 flex items-start gap-4">
                  <CheckCircle size={22} weight="fill" className="text-brand mt-0.5 shrink-0" />
                  <div className="text-ink body-md !text-ink font-medium">{b}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid={`service-${slug}-outcomes`}>
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <div className="overline mb-5">Why it matters</div>
              <h2 className="h-section text-balance">Outcomes we deliver — not features we advertise.</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {service.outcomes.map((o, i) => (
              <Reveal key={o.label} delay={i * 0.08}>
                <div className="p-8 rounded-3xl bg-neutral-50 border border-neutral-200 h-full">
                  <div className="text-brand font-display font-black text-4xl mb-4">0{i + 1}</div>
                  <h3 className="font-display font-bold text-lg text-ink">{o.label}</h3>
                  <p className="body-md mt-2">{o.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ink text-white">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="overline text-brand-soft mb-5">Our standards</div>
                <h2 className="h-section text-white text-balance">The same operating standard for every service — including {service.name}.</h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={0.15}>
                <Link to="/about" className="text-white/80 hover:text-white btn-ghost">
                  Read our full standard →
                </Link>
              </Reveal>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
            {TRUST_POINTS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05}>
                <div className="flex items-start gap-3">
                  <IconResolver name={t.icon} size={22} className="text-brand-soft mt-0.5" />
                  <div>
                    <div className="font-semibold text-white text-sm">{t.title}</div>
                    <div className="text-xs text-white/60 mt-1">{t.body}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <Reveal>
              <div className="overline mb-5">Related services</div>
              <h2 className="h-section text-balance">Other Inclusive Mobility Group services you may need.</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-${slug}-related-${s.slug}`}
                  className="group block h-full rounded-3xl bg-neutral-50 border border-neutral-200 p-7 hover:bg-white hover:border-brand hover:shadow-lg transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                    <IconResolver name={ICON_MAP[s.slug]} size={20} className="text-brand" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink">{s.name}</h3>
                  <p className="body-md mt-2 line-clamp-2">{s.tagline}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-brand font-semibold text-sm">
                    Learn more <ArrowRight size={14} weight="bold" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
